// Take payment for a saved record, through the gateway this project's
// manager connected in Advanced → Payments.
//
// Unlike the other blocks in this folder, nothing here talks to Proteus
// Vision: it charges for a record the APPLICATION already holds, and the
// money goes to the project owner's own merchant account.
//
// THE BLOCK SENDS NO AMOUNT. It posts which record is being paid for plus
// the author's column bindings; the server re-reads that record and resolves
// the amount itself. That is why the settings below are column NAMES — see
// `packages/page-runtime/src/payments/checkout.ts` and
// `backend/app/services/app_payments/checkout.py`.

import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  awaitSettlement, fetchPaymentConfig, PaymentCancelled, PaymentFailed, placeCashOnDelivery,
  startPayment,
  type CashOnDeliveryResult, type CheckoutOrder, type PaymentApi,
  type PaymentPublicConfig,
} from '@twasta/page-runtime'
import { useRuntime } from '@/pages/workbench/smartpage-preview/RuntimeContext'
import { useProjectStore } from '@/store/projectStore'
import { getSiteToken } from '@/api/website'
import { getMyAccess } from '@/api/team'
import type { PaymentCheckoutBlock } from '@/pages/designers/page/types'
import { injectEcomStyles } from './styles'

const APP_BASE = (import.meta.env?.BASE_URL || '/').replace(/\/$/, '')

/**
 * A `fetch` that carries whichever session this surface actually has.
 *
 * On a published site the visitor holds an opaque bearer token per
 * (owner, project); in the in-app preview and the Run view the platform
 * cookie is the session. Sending both is safe and means one code path: the
 * server resolves the strongest claim it can verify.
 */
function makePaymentApi(ownerId: string, project: string): PaymentApi {
  const token = getSiteToken(ownerId, project)
  return {
    base: `${APP_BASE}/api/app-payments/${encodeURIComponent(ownerId)}`
          + `/${encodeURIComponent(project)}`,
    fetch: (url, init) => fetch(url, {
      ...init,
      credentials: 'include',
      headers: {
        ...(init?.headers || {}),
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    }),
  }
}

/** The author's camelCase block settings as the server's binding shape. */
function toBinding(block: PaymentCheckoutBlock): Record<string, unknown> {
  const wb = block.writeback || {}
  return {
    amount_column: block.amountColumn || '',
    currency: block.currency || '',
    currency_column: block.currencyColumn || '',
    reference_column: block.referenceColumn || '',
    owner_column: block.ownerColumn || '',
    payer_name_column: block.payerNameColumn || '',
    payer_email_column: block.payerEmailColumn || '',
    payer_contact_column: block.payerContactColumn || '',
    writeback: {
      status_column: wb.statusColumn || '',
      status_value: wb.statusValue || 'PAID',
      failed_value: wb.failedValue || 'FAILED',
      payment_id_column: wb.paymentIdColumn || '',
      paid_amount_column: wb.paidAmountColumn || '',
      paid_at_column: wb.paidAtColumn || '',
      method_column: wb.methodColumn || '',
    },
  }
}

export default function PaymentCheckoutRenderer(
  { block }: { block: PaymentCheckoutBlock },
) {
  injectEcomStyles()
  const runtime = useRuntime()
  const projectAccess = useProjectStore(s => s.projectAccess)

  const project = runtime.project

  // Which application is being paid. A shared project is owned by someone
  // other than the person looking at it, so the current user's own id is
  // never the right answer — it has to be the OWNER.
  //
  //  1. A site visitor's runtime names the owner outright.
  //  2. The workbench already asked, for the project it has open. The
  //     project_id check matters: the store holds whichever project was
  //     opened last, which on a preview of a different one would charge
  //     against the wrong merchant account.
  //  3. Otherwise ask. The RUN view never populates that store — it is the
  //     workbench's — so without this the block could not name the
  //     application it was sitting in and refused to take payment at all.
  const siteOwner = runtime.dataAccess?.mode === 'site' ? runtime.dataAccess.userId : ''
  const storeOwner = projectAccess?.project_id === project
    ? (projectAccess?.owner_user_id || '')
    : ''
  const [fetchedOwner, setFetchedOwner] = useState('')
  const ownerId = siteOwner || storeOwner || fetchedOwner

  useEffect(() => {
    if (siteOwner || storeOwner || !project) return
    let alive = true
    getMyAccess(project)
      .then((r) => { if (alive) setFetchedOwner(r.owner_user_id || '') })
      // Not a member, or not signed in: leave it unresolved and let the
      // guard below say so, rather than charging against a guess.
      .catch(() => { if (alive) setFetchedOwner('') })
    return () => { alive = false }
  }, [siteOwner, storeOwner, project])

  // The record: the page variable an earlier block wrote, or — on a
  // "pay for this order" page opened from the order list — the URL.
  const fromVar = String(runtime.vars?.[block.txnIdVar || ''] ?? '')
  const fromQuery = useMemo(() => {
    if (!block.txnIdFromQuery || typeof window === 'undefined') return ''
    try { return new URLSearchParams(window.location.search).get(block.txnIdFromQuery) || '' } catch { return '' }
  }, [block.txnIdFromQuery])
  const txnId = fromVar || fromQuery
  // Where a finished payment goes; `{{txn}}` = this record.
  const successHref = (block.onSuccessNavigateTo || '').replace(/\{\{\s*txn\s*\}\}/g, encodeURIComponent(txnId))
  const [config, setConfig] = useState<PaymentPublicConfig | null>(null)
  const [busy, setBusy] = useState(false)
  const [order, setOrder] = useState<CheckoutOrder | null>(null)
  const [pending, setPending] = useState(false)
  const [cod, setCod] = useState<CashOnDeliveryResult | null>(null)
  const [error, setError] = useState('')
  // The last attempt was refused by the gateway: the ORDER stands, payment
  // is still owed — offer to retry rather than reading as a dead end.
  const [failed, setFailed] = useState('')
  // Cash on delivery chosen, its note shown, not yet confirmed.
  const [codAsk, setCodAsk] = useState(false)

  const api = useMemo(
    () => (ownerId && project ? makePaymentApi(ownerId, project) : null),
    [ownerId, project],
  )

  useEffect(() => {
    if (!api) return
    let alive = true
    fetchPaymentConfig(api)
      .then(cfg => { if (alive) setConfig(cfg) })
      // A shop with no gateway is a legitimate state, not an error — the
      // block then offers only what it can honour.
      .catch(() => { if (alive) setConfig(null) })
    return () => { alive = false }
  }, [api])

  const pay = useCallback(async () => {
    if (!api) return
    setError('')
    setFailed('')
    setBusy(true)
    try {
      const result = await startPayment(api, {
        objName: block.objName,
        txnId,
        binding: toBinding(block),
        description: block.heading || undefined,
      })
      setOrder(result.order)
      setPending(result.pending)
      if (result.pending) {
        // The gateway took it and our confirmation did not land. The webhook
        // is the authoritative path, so wait for it rather than telling the
        // customer their payment failed when it may well have succeeded.
        const settled = await awaitSettlement(api, result.order.id)
        if (settled) { setOrder(settled); setPending(settled.status !== 'paid') }
      }
      if (!result.pending && successHref) {
        window.location.href = successHref
      }
    } catch (err) {
      if (err instanceof PaymentCancelled) setError('')
      else if (err instanceof PaymentFailed) setFailed(err.message)
      else setError(err instanceof Error ? err.message : 'The payment could not be completed.')
    } finally {
      setBusy(false)
    }
  }, [api, block, txnId, successHref])

  // Cash on delivery goes through the payment rail too: the record's payment
  // columns are writable only there, so this is not a save of the record.
  // The server stamps the MODEL's `cod_status_value` — nothing here decides
  // what the order will say.
  const placeCod = useCallback(async () => {
    if (!api) return
    setError('')
    setBusy(true)
    try {
      const result = await placeCashOnDelivery(api, {
        objName: block.objName, txnId, binding: toBinding(block),
      })
      setCod(result)
      if (successHref) window.location.href = successHref
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Cash on delivery could not be recorded.')
    } finally {
      setBusy(false)
    }
  }, [api, block, txnId, successHref])

  // A payment step that waits for its order: nothing at all until the page
  // variable names a record (the designer canvas draws this block itself).
  if (block.hideUntilRecord && !txnId) return null

  // ── states the author needs to see while building ──────────────────────
  if (!block.objName || !block.amountColumn) {
    return (
      <div className="paychk__note" data-ecom-block="payment_checkout">
        <strong>Payment checkout</strong> — choose the object and the column
        holding the amount in the properties panel.
      </div>
    )
  }
  if (!ownerId || !project) {
    return (
      <div className="paychk__note" data-ecom-block="payment_checkout">
        <strong>Payment checkout</strong> — this preview does not know which
        application it belongs to, so it cannot take payment here.
      </div>
    )
  }

  const paid = order?.status === 'paid'
  const canPay = !!config?.enabled && !!txnId && !busy && !paid
  const codOnly = !!block.showCodOption && !config?.enabled && !!config?.cod_enabled

  return (
    <section className="paychk" data-ecom-block="payment_checkout">
      {block.heading && <h3 className="paychk__title">{block.heading}</h3>}
      {block.description && <p className="paychk__muted">{block.description}</p>}

      {cod ? (
        <div className="paychk__success">
          <strong>{block.successTitle && block.successTitle !== 'Payment received'
            ? block.successTitle : 'Order confirmed'}</strong>
          <div className="paychk__muted">
            {block.successBody
              || `${cod.reference || 'Your order'} will be paid in cash on delivery.`}
          </div>
        </div>
      ) : paid ? (
        <div className="paychk__success">
          <strong>{block.successTitle || 'Payment received'}</strong>
          <div className="paychk__muted">
            {block.successBody
              || `${order?.reference || 'Your order'} is paid.`}
          </div>
          {/* The one honest thing to say when the money landed and the
              application has not caught up: say it, rather than showing a
              paid order that still reads unpaid everywhere else. */}
          {order && !order.written_back && (
            <div className="paychk__muted" style={{ marginTop: 6 }}>
              We have your payment. The order is being updated — refresh in a
              moment if it still shows as unpaid.
            </div>
          )}
        </div>
      ) : (
        <>
          {!txnId && (
            <p className="paychk__muted">
              Nothing to pay for yet — place the order first.
            </p>
          )}

          {config && !config.configured && !codOnly && (
            <p className="paychk__muted">
              Online payment is not set up for this application yet.
            </p>
          )}

          {failed && (
            <div className="paychk__error" role="alert" style={{ marginBottom: 10 }}>
              <strong>Payment failed.</strong> {failed} Your order is saved — no money was
              taken. You can try again, choose another way to pay, or pay later from
              My orders.
            </div>
          )}

          {config?.enabled && (
            <button type="button" className="paychk__btn paychk__btn--primary"
                    disabled={!canPay} onClick={() => void pay()}>
              {busy ? 'Opening the payment window…'
                : failed ? 'Retry payment' : (block.buttonLabel || 'Pay now')}
            </button>
          )}

          {/* Cash on delivery needs no gateway, which is why it can be the
              only thing on offer. A note (packing / unboxing instructions)
              is shown and confirmed first when the page declares one. */}
          {block.showCodOption && config?.cod_enabled && !codAsk && (
            <button type="button" className="paychk__btn" style={{ marginLeft: config?.enabled ? 8 : 0 }}
                    disabled={!txnId || busy}
                    onClick={() => { if (block.codNote) setCodAsk(true); else void placeCod() }}>
              {busy ? 'Confirming…' : (block.codLabel || 'Cash on delivery')}
            </button>
          )}
          {codAsk && (
            <div className="paychk__note" role="region" aria-label="Cash on delivery"
                 style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 10 }}>
              <strong>{block.codLabel || 'Cash on delivery'}</strong>
              {String(block.codNote || '').split(/\n\s*\n/).map((para, i) => (
                <p key={i} style={{ margin: 0 }}>{para.trim()}</p>
              ))}
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <button type="button" className="paychk__btn paychk__btn--primary"
                        disabled={!txnId || busy} onClick={() => void placeCod()}>
                  {busy ? 'Confirming…' : 'Confirm cash on delivery'}
                </button>
                <button type="button" className="paychk__btn" disabled={busy}
                        onClick={() => setCodAsk(false)}>
                  Choose another way to pay
                </button>
              </div>
            </div>
          )}

          {codOnly && (
            <p className="paychk__muted" style={{ marginTop: 8 }}>
              This shop currently takes cash on delivery only.
            </p>
          )}

          {pending && (
            <p className="paychk__muted" style={{ marginTop: 8 }}>
              Your bank approved the payment and we are confirming it. This
              page will update; you will also get an email.
            </p>
          )}
        </>
      )}

      {error && <div className="paychk__error" style={{ marginTop: 8 }}>{error}</div>}
      {!paid && !cod && (block.policyLinks || []).length > 0 && (
        <p className="paychk__muted" style={{ marginTop: 12, fontSize: '0.85rem' }}>
          {block.policyText || 'By placing your order you agree to our'}{' '}
          {(block.policyLinks || []).map((l, i, all) => (
            <span key={l.href}>
              <a href={l.href} target="_blank" rel="noreferrer" style={{ color: 'inherit', textDecoration: 'underline' }}>{l.label}</a>
              {i < all.length - 2 ? ', ' : i === all.length - 2 ? ' and ' : '.'}
            </span>
          ))}
        </p>
      )}
      {block.continueHref && (failed || paid || cod) && (
        <a href={block.continueHref} className="paychk__btn"
           style={{ display: 'inline-block', marginTop: 12, textDecoration: 'none' }}>
          {block.continueLabel || 'Continue shopping'}
        </a>
      )}
    </section>
  )
}
